import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  BehaviorSubject,
  Subscription,
  Observable,
  combineLatest,
  map,
  take,
} from 'rxjs';
import {
  NodeItem,
  StateService,
  ToolService,
  RouterService,
  ConstructionSiteState,
} from '../../public-api';
import { SortOption } from '../models/sort-option';
import { ModalController } from '@ionic/angular';
import { MoveFolderComponent } from '../move-folder/move-folder.component';

@Component({
  selector: 'lib-file-tree',
  templateUrl: './file-tree.component.html',
  styleUrls: ['./file-tree.component.scss'],
  standalone: false,
})
export class FileTreeComponent implements OnInit {
  @Input()
  itemsToMove: NodeItem[] = [];
  @Input()
  isMoveTree: boolean = false;
  @Input()
  conSitesMode: boolean = false;
  @Output()
  currentFolderNodeItemChanged: EventEmitter<NodeItem> =
    new EventEmitter<NodeItem>();
  @Output()
  openFile: EventEmitter<string> = new EventEmitter<string>();
  @Output()
  selectedNodeItemsChanged: EventEmitter<NodeItem[]> = new EventEmitter<
    NodeItem[]
  >();
  topBreadCrumbDisabled: boolean = false; // during upload switching consities is not allowed
  selectedNodeItems: NodeItem[] = [];
  ALLITEMS: string = '';
  public currentFolderId$ = new BehaviorSubject<string>(this.state.magicNumber);
  constructionSiteStates = [
    { label: 'Aktiv', value: ConstructionSiteState.$0 },
    { label: 'Archiviert', value: ConstructionSiteState.$1 },
  ];
  selectedState: BehaviorSubject<ConstructionSiteState> =
    new BehaviorSubject<ConstructionSiteState>(ConstructionSiteState.$0);
  showFilters: boolean;

  breadcrumbs: NodeItem[] = [];
  searchTerm$ = new BehaviorSubject<string>('');
  subs: Subscription[] = [];
  sortOptions: SortOption[] = [
    {
      property: 'createdUtc',
      label: 'Datum absteigend',
      isAscending: false,
    },
    {
      property: 'createdUtc',
      label: 'Datum aufsteigend',
      isAscending: true,
    },

    { property: 'displayName', label: 'Name aufsteigend', isAscending: true },
    { property: 'displayName', label: 'Name absteigend', isAscending: false },
  ];
  selectedSortOption: BehaviorSubject<SortOption>;
  currentItems$!: Observable<NodeItem[]>;

  constructor(
    public state: StateService,
    public tools: ToolService,
    public routerService: RouterService,
    private modalController: ModalController
  ) {}

  async ngOnInit() {
    //if (this.startFolderId) this.currentFolderId$.next(this.startFolderId);
    let defaultSortOption = this.sortOptions.find(
      (x) => x.label == 'Datum absteigend'
    );

    if (!this.conSitesMode) {
      const defaultSortOptionLabel = await this.state.readFromSecureStorage(
        this.state.SORTINGPREFERENCE
      );
      defaultSortOption =
        this.sortOptions.find((x) => x.label == defaultSortOptionLabel) ??
        defaultSortOption;
    }

    this.selectedSortOption = new BehaviorSubject<SortOption>(
      defaultSortOption
    );

    this.ALLITEMS = this.conSitesMode ? 'Baustellen' : 'Ordner';
    this.breadcrumbs.push({ displayName: this.ALLITEMS });
    if (this.isMoveTree) {
      this.currentFolderNodeItemChanged.emit(null);
    }
    this.setupCurrentItems();
    this.showFilters = this.state.isBigScreen;
  }

  private setupCurrentItems(): void {
    this.currentItems$ = combineLatest([
      this.state.nodeItems$,
      this.currentFolderId$,
      this.selectedSortOption,
      this.searchTerm$,
      this.selectedState,
    ]).pipe(
      map(([nodes, currentFolderId, sortOption, searchTerm, state]) => {
        let items = nodes.filter(
          (n) =>
            n.parentId === currentFolderId &&
            !n.machineId &&
            !n.userId &&
            !n.constructionDayReportId &&
            !n.absenceEntryId
        );
        if (this.conSitesMode) {
          items = items.filter(
            (n) => n.constructionSiteId != this.state.magicNumber
          );
        } else {
          items = items.filter(
            (n) => n.constructionSiteId == this.state.magicNumber
          );
        }
        // Suche
        if (searchTerm) {
          if (currentFolderId == this.state.magicNumber) {
            // search in all folders
            if (this.conSitesMode) {
              items = nodes.filter(
                (n) =>
                  n.displayName
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()) &&
                  n.constructionSiteId != this.state.magicNumber
              );
            } else {
              items = nodes.filter(
                (n) =>
                  n.displayName
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()) &&
                  n.constructionSiteId == this.state.magicNumber
              );
            }
          } else {
            items = items.filter((n) =>
              n.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
            );
          }
        }

        items = items.filter((x) => x.state == state);

        // Sortierung
        if (sortOption) {
          items = [...items].sort((a, b) => {
            const prop = sortOption.property as keyof NodeItem;
            let valA = a[prop];
            let valB = b[prop];

            if (valA === null || valA === undefined) valA = '';
            if (valB === null || valB === undefined) valB = '';

            if (valA < valB) return sortOption.isAscending ? -1 : 1;
            if (valA > valB) return sortOption.isAscending ? 1 : -1;
            return 0;
          });
        }
        return items;
      })
    );
  }

  ngOnDestroy() {
    this.subs.forEach((x) => x.unsubscribe());
  }

  allSelected = false;

  isSelected(doc: NodeItem): boolean {
    return this.selectedNodeItems.includes(doc);
  }

  toggleSelection(doc: NodeItem) {
    if (this.isSelected(doc)) {
      this.selectedNodeItems.splice(this.selectedNodeItems.indexOf(doc), 1);
    } else {
      this.selectedNodeItems.push(doc);
    }
    this.selectedNodeItemsChanged.emit(this.selectedNodeItems);
    this.updateAllSelected();
  }

  toggleAll() {
    this.currentItems$.pipe(take(1)).subscribe((items) => {
      if (this.allSelected) {
        // abwählen
        this.selectedNodeItems = [];
      } else {
        // alle auswählen
        items.forEach((doc) => this.selectedNodeItems.push(doc));
      }
      this.allSelected = !this.allSelected;
      this.selectedNodeItemsChanged.emit(this.selectedNodeItems);
    });
  }

  updateAllSelected() {
    this.currentItems$.pipe(take(1)).subscribe((items) => {
      this.allSelected = items.every((doc) =>
        this.selectedNodeItems.includes(doc)
      );
    });
  }

  unselectAll() {
    this.selectedNodeItems = [];
    this.allSelected = false;
    this.selectedNodeItemsChanged.emit(this.selectedNodeItems);
  }

  itemClicked(item: NodeItem) {
    if (this.itemsToMove.includes(item)) return;
    this.unselectAll();
    if (item.parentId == this.state.magicNumber && !this.isMoveTree) {
      this.state.changeSelectedConSite(item.constructionSiteId);
    }
    if (!item.fileExtension) {
      this.currentFolderNodeItemChanged.emit(item);
      this.openFolder(item);
      if (this.searchTerm$.value != '') this.searchTerm$.next('');
    } else {
      if (!this.isMoveTree) {
        this.openFile.emit(item.id);
      }
    }
  }

  getIcon(doc: NodeItem): string {
    if (this.state.supportedPhotoFileTypes.includes(doc.fileExtension))
      return 'image-outline';
    if (this.state.supportedDocumentFileTypes.includes(doc.fileExtension))
      return 'document-outline';
    if (this.state.supportedVideoFileTypes.includes(doc.fileExtension))
      return 'film-outline';
    if (this.conSitesMode && this.breadcrumbs.length <= 1)
      return 'construct-outline';
    return 'folder-outline';
  }

  async sortChange(event: any) {
    const sortOption = this.sortOptions.find(
      (x) => x.label == event.detail.value
    );
    if (sortOption) {
      this.selectedSortOption.next(sortOption);
      if (!this.conSitesMode) {
        await this.state.saveToSecureStorage(
          this.state.SORTINGPREFERENCE,
          sortOption.label
        );
      }
    }
  }

  selectedStateChange(state: ConstructionSiteState) {
    this.unselectAll();
    this.selectedState.next(state);
  }

  openFolder(folder: NodeItem): void {
    this.breadcrumbs.push(folder);
    this.currentFolderId$.next(folder.id ?? null);
  }

  goBack(): void {
    if (this.breadcrumbs.length > 1) {
      const preLastNode = this.breadcrumbs[this.breadcrumbs.length - 2];
      this.goToBreadcrumb(preLastNode);
    } else {
      this.routerService.goBack();
    }
  }

  setSearchTerm(term: string): void {
    this.searchTerm$.next(term);
  }

  goToBreadcrumb(node: NodeItem): void {
    if (this.topBreadCrumbDisabled && node.displayName == this.ALLITEMS) {
      this.tools.showInfoToast(
        'Baustellen können während des Hochladens nicht gewechselt werden'
      );
      return;
    }
    this.unselectAll();
    this.currentFolderNodeItemChanged.emit(node);
    const index = this.breadcrumbs.indexOf(node);
    const length = this.breadcrumbs.length;
    for (let i = 1; i < length - index; i++) {
      this.breadcrumbs.pop();
    }
    this.currentFolderId$.next(node.id ?? this.state.magicNumber);
  }

  // Buttons

  async addFolder() {
    await this.tools
      .presentInputAlert('Neuer Ordner', '', 'Ordnername')
      .then((x) => {
        this.state.addFolder(x, this.currentFolderId$.value);
      });
  }

  async renameFolder() {
    if (
      this.selectedNodeItems[0].parentId == this.state.magicNumber &&
      this.selectedNodeItems[0].constructionSiteId != this.state.magicNumber
    ) {
      await this.state.findConSiteAndEdit(
        this.selectedNodeItems[0].constructionSiteId
      );
      this.unselectAll();
      return;
    }
    let objectName = this.selectedNodeItems[0].fileExtension
      ? 'Datei'
      : 'Ordner';
    await this.tools
      .presentInputAlert(
        objectName + ' umbenennen',
        '',
        'Ordnername',
        'Umbenennen',
        this.selectedNodeItems[0].displayName
      )
      .then((x) => {
        if (!x) return;
        this.selectedNodeItems[0].displayName = x;
        this.state.renameNodeItem(this.selectedNodeItems[0]);
        this.unselectAll();
      });
  }

  async deleteNodes() {
    await this.state.deleteNodes(this.selectedNodeItems.map((x) => x.id));
    this.unselectAll();
  }

  async openMoveFolderModal() {
    const modal = await this.modalController.create({
      component: MoveFolderComponent,
      componentProps: {
        itemsToMove: this.selectedNodeItems,
        conSitesMode: this.conSitesMode,
      },
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm') {
      console.log(`Moved all to folder ${data}!`);
      this.selectedNodeItems = [];
      this.selectedNodeItemsChanged.emit(this.selectedNodeItems);
      this.unselectAll();
    }
  }
}
